import React, { useState, useEffect, useCallback, useMemo } from "react";
import Select from "react-select";
import {
    Card,
    CardBody,
    Container,
    Row,
    Col,
    Label,
    Input,
    Button,
    FormGroup,
    Form,
    InputGroup,
    FormFeedback,
} from "reactstrap";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { PatternFormat } from "react-number-format";
import * as Yup from "yup";
import Loaders from "components/Common/Loaders";
import { urlEndPoint, allowOnlyNumber, toPhoneDigits } from "helpers/GeneralHelper";
import {
    getUserById,
    fetchCommonWorkShiftList,
    fetchCommonStatusList,
    fetchCommonUserTypeList,
    createUser,
    updateUser,
} from "model/UserModel";
import { submitUserTeamFilterData } from "model/UserTeamModel";
import { getUserTypeById } from "model/UserTypeModel";

import "./manage-user-add-edit.scss";

const unwrapArray = (payload) => {
    if (Array.isArray(payload)) return payload;
    const payloadRoot = payload?.data ?? payload;
    if (Array.isArray(payloadRoot)) return payloadRoot;
    const nestedRecords =
        payloadRoot?.records ?? payloadRoot?.rows ?? payloadRoot?.items;
    return Array.isArray(nestedRecords) ? nestedRecords : [];
};

const normalizeId = (value) => String(value ?? "").trim();

/** e.g. user@gmail.com, user@ancile.services */
const MANAGE_USER_EMAIL_REGEX = /^[\w-.]+@([\w-]+\.)+[\w-]{2,}$/i;

const buildManageUserFormSchema = (isEdit = false) => {
    const baseFields = {
        userTypeId: Yup.string().trim().required("User type is required"),
        name: Yup.string().trim().required("Name is required"),
        email: Yup.string()
            .trim()
            .required("Email is required")
            .matches(MANAGE_USER_EMAIL_REGEX, "Enter a valid email"),
    };

    if (isEdit) {
        return Yup.object().shape({
            ...baseFields,
            password: Yup.string().trim(),
            repassword: Yup.string()
                .trim()
                .when("password", {
                    is: (value) => Boolean(String(value || "").trim()),
                    then: (schema) =>
                        schema
                            .required("Re-password is required")
                            .oneOf(
                                [Yup.ref("password")],
                                "Password and re-password do not match"
                            ),
                }),
        });
    }

    return Yup.object().shape({
        ...baseFields,
        password: Yup.string().trim().required("Password is required"),
        repassword: Yup.string()
            .trim()
            .required("Re-password is required")
            .oneOf([Yup.ref("password")], "Password and re-password do not match"),
    });
};

const buildManageUserValidationValues = (form = {}) => ({
    userTypeId: normalizeId(form?.userTypeId),
    name: String(form?.name ?? "").trim(),
    email: String(form?.email ?? "").trim(),
    password: String(form?.password ?? ""),
    repassword: String(form?.repassword ?? ""),
});

/** Map `common/status` payload to `{ value, label }` (value lowercased for consistency with user API). */
const normalizeStatusOptions = (payload) => {
    const list = unwrapArray(payload);
    const mapped = list
        .map((item) => {
            const raw = item?.value ?? item?.id ?? item?.code ?? item?.key ?? "";
            const value = String(raw).trim().toLowerCase();
            if (!value) return null;
            const label =
                item?.label ??
                item?.name ??
                item?.title ??
                item?.display ??
                item?.text ??
                value;
            return { value, label: String(label) };
        })
        .filter(Boolean);
    if (mapped.length) return mapped;
    return [
        { value: "active", label: "Active" },
        { value: "deactive", label: "Deactive" },
        { value: "blocked", label: "Blocked" },
    ];
};

/** Same as `CandidateHireStatusAddEditModal` — `className="select2-selection"` + portal z-index. */
const selectZIndexStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
};

const buildUserImageInlineUrl = (userImageUrl = "") => {
    const raw = String(userImageUrl ?? "").trim();
    if (!raw) return "";
    if (/\/inline\/?$/i.test(raw)) return raw.replace(/\/$/, "");
    return `${raw.replace(/\/$/, "")}/inline`;
};

const resolveManageUserTypeId = (userTypeId = "") => {
    const typeRaw = normalizeId(userTypeId);
    if (!typeRaw) return "";
    if (/^\d+$/.test(typeRaw)) return String(Number(typeRaw));
    return typeRaw;
};

const appendManageUserFormDataValue = (formDataBody, key, value) => {
    if (value === undefined || value === null) {
        formDataBody.append(key, "");
        return;
    }
    formDataBody.append(key, value);
};

const isSuccessResponse = (res) =>
    res?.success !== false && (res?.success || res?.isSuccess || res?.data != null);

const MANAGE_USER_PHONE_INPUT_FORMAT = "+1 (###) ###-####";

/** Digits only for phone fields (`allowOnlyNumber` strips non-numeric; drop decimals). */
const phoneDigitsOnly = (value) =>
    String(allowOnlyNumber(value ?? ""))
        .replace(/\./g, "")
        .replace(/[^0-9]/g, "");

/** Build `multipart/form-data` body for `POST User/system` / `PUT User/system/{id}`. */
const buildManageUserFormData = (formData) => {
    const typeId = resolveManageUserTypeId(formData.userTypeId);
    const teamId = (formData.teamIds || []).map(normalizeId).filter(Boolean).join(",");
    const companyIds = (formData.companyIds || []).map(String).join(",");
    const clientAccess = (formData.clientIds || []).map(String).join(",");
    const password = String(formData.password || "").trim();
    const fd = new FormData();

    appendManageUserFormDataValue(fd, "Name", String(formData.name || "").trim());
    appendManageUserFormDataValue(fd, "Email", String(formData.email || "").trim());
    appendManageUserFormDataValue(fd, "TypeId", typeId);
    appendManageUserFormDataValue(fd, "TeamId", teamId);
    appendManageUserFormDataValue(fd, "Phone", toPhoneDigits(formData.phone || "").trim());
    appendManageUserFormDataValue(fd, "PhoneExt", String(formData.phoneExt || "").trim());
    appendManageUserFormDataValue(fd, "Status", String(formData.status || "").trim());
    appendManageUserFormDataValue(fd, "WorkShiftId", String(formData.workShiftId || "").trim());
    appendManageUserFormDataValue(fd, "Layout", String(formData.layout || "").trim());
    appendManageUserFormDataValue(fd, "Zone", String(formData.zone || "").trim());
    appendManageUserFormDataValue(fd, "Password", password);
    appendManageUserFormDataValue(fd, "CompanyIds", companyIds);
    appendManageUserFormDataValue(fd, "ClientAccess", clientAccess);
    appendManageUserFormDataValue(fd, "MaskName", String(formData.maskName || "").trim());
    appendManageUserFormDataValue(fd, "AboutUser", String(formData.about || "").trim());

    if (formData.profileFile instanceof File) {
        fd.append("UserImageFile", formData.profileFile);
    } else {
        appendManageUserFormDataValue(fd, "UserImageFile", "");
    }

    return fd;
};

const ManageUserAddEdit = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const isEditMode = Boolean(normalizeId(userId));

    const [loadingPage, setLoadingPage] = useState(!!isEditMode);
    const [loadingOptions, setLoadingOptions] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showRepassword, setShowRepassword] = useState(false);
    const [errors, setErrors] = useState({});

    const manageUserFormSchema = useMemo(
        () => buildManageUserFormSchema(isEditMode),
        [isEditMode]
    );

    const [userTypes, setUserTypes] = useState([]);
    const [workShiftOptions, setWorkShiftOptions] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);
    const [teamOptions, setTeamOptions] = useState([]);
    const [multipleTeam, setMultipleTeam] = useState(true);

    const userTypeSelectOptions = useMemo(
        () =>
            userTypes.map((item) => ({
                value: item.id,
                label: item.name || item.id,
            })),
        [userTypes]
    );

    const workShiftSelectOptions = useMemo(
        () =>
            workShiftOptions.map((item) => {
                const id = normalizeId(item?.id);
                const display =
                    item?.display ??
                    item?.label ??
                    item?.name ??
                    item?.title ??
                    id;
                return {
                    value: id,
                    label: String(display),
                };
            }),
        [workShiftOptions]
    );

    const [formData, setFormData] = useState({
        id: "",
        userTypeId: "",
        workShiftId: "",
        name: "",
        email: "",
        password: "",
        repassword: "",
        maskName: "",
        phone: "",
        phoneExt: "",
        about: "",
        teamIds: [],
        status: "active",
        typeLabel: "",
        blocked: false,
        showClient: false,
        companyIds: [],
        clientIds: [],
        profileImage: "",
        profileImageUrl: "",
        profileFile: null,
        layout: "",
        zone: "",
    });

    const [avatarPreviewUrl, setAvatarPreviewUrl] = useState(null);
    useEffect(() => {
        if (!(formData.profileFile instanceof File)) {
            setAvatarPreviewUrl(null);
            return undefined;
        }
        const url = URL.createObjectURL(formData.profileFile);
        setAvatarPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [formData.profileFile]);

    const displayAvatarUrl = useMemo(() => {
        if (avatarPreviewUrl) return avatarPreviewUrl;
        return buildUserImageInlineUrl(formData.profileImageUrl);
    }, [avatarPreviewUrl, formData.profileImageUrl]);

    const clearFieldError = (fieldName) => {
        if (!errors[fieldName]) return;
        setErrors((previousErrors) => ({ ...previousErrors, [fieldName]: "" }));
    };

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        if (type === "checkbox") {
            setFormData((previousForm) => ({
                ...previousForm,
                [name]: checked,
            }));
        } else {
            setFormData((previousForm) => ({
                ...previousForm,
                [name]: value,
            }));
        }
        if (manageUserFormSchema.fields[name]) {
            clearFieldError(name);
        }
    };

    const handlePhoneValueChange = (values) => {
        setFormData((previousForm) => ({
            ...previousForm,
            phone: toPhoneDigits(values?.value || ""),
        }));
    };

    /** PatternFormat truncates to 10 digits before onValueChange; strip +1 on paste first. */
    const handlePhonePaste = (event) => {
        const pasted = event.clipboardData?.getData("text") || "";
        const digits = String(pasted).replace(/\D/g, "");
        if (digits.length === 11 && digits.startsWith("1")) {
            event.preventDefault();
            setFormData((previousForm) => ({
                ...previousForm,
                phone: toPhoneDigits(pasted),
            }));
        }
    };

    const handlePhoneExtFieldChange = (event) => {
        const { name, value } = event.target;
        setFormData((previousForm) => ({
            ...previousForm,
            [name]: phoneDigitsOnly(value),
        }));
    };

    const applyUserTypeTeamRule = useCallback(async (userTypeId) => {
        const normalizedUserTypeId = normalizeId(userTypeId);
        if (!normalizedUserTypeId) {
            setMultipleTeam(true);
            return;
        }
        // const userTypeApiResponse = await getUserTypeById(normalizedUserTypeId);
        // const userTypePayload = userTypeApiResponse?.data ?? userTypeApiResponse;
        // setMultipleTeam(Boolean(userTypePayload?.multipleTeam ?? true));
    }, []);

    const loadReferenceData = useCallback(async () => {
        setLoadingOptions(true);
        try {
            const [
                userTypesApiResponse,
                workShiftsApiResponse,
                accountStatusesApiResponse,
                teamsApiResponse,
            ] = await Promise.all([
                fetchCommonUserTypeList(),
                fetchCommonWorkShiftList(),
                fetchCommonStatusList(),
                submitUserTeamFilterData({
                    statusId: "active",
                }),
            ]);

            const userTypeRows = unwrapArray(
                userTypesApiResponse?.data ?? userTypesApiResponse
            );
            setUserTypes(
                userTypeRows
                    .map((userTypeRow) => ({
                        id: normalizeId(userTypeRow?.id ?? userTypeRow?._id),
                        name:
                            userTypeRow?.display ??
                            userTypeRow?.name ??
                            userTypeRow?.title ??
                            "",
                    }))
                    .filter((row) => row.id)
            );

            const workShiftRows = unwrapArray(
                workShiftsApiResponse?.data ?? workShiftsApiResponse
            );
            setWorkShiftOptions(
                workShiftRows
                    .map((workShiftRow) => {
                        const shiftId = normalizeId(
                            workShiftRow?.id ?? workShiftRow?._id
                        );
                        const displayLabel =
                            workShiftRow?.display ??
                            workShiftRow?.name ??
                            workShiftRow?.title ??
                            workShiftRow?.workShiftTitle ??
                            workShiftRow?.workShiftTimeDisplay ??
                            shiftId;
                        return { id: shiftId, display: displayLabel };
                    })
                    .filter((row) => row.id)
            );

            setStatusOptions(
                normalizeStatusOptions(
                    accountStatusesApiResponse?.data ?? accountStatusesApiResponse
                )
            );

            const teamsResponsePayload = teamsApiResponse?.data ?? {};
            const teamRowsFromApi =
                teamsResponsePayload?.records ??
                unwrapArray(teamsApiResponse?.data ?? teamsApiResponse);
            setTeamOptions(
                (Array.isArray(teamRowsFromApi) ? teamRowsFromApi : []).map(
                    (teamRow) => ({
                        id: normalizeId(teamRow?.id ?? teamRow?._id),
                        name: teamRow?.name ?? teamRow?.title ?? "",
                    })
                )
                    .filter((row) => row.id)
            );
        } catch (loadError) {
            toast.error(loadError?.message || "Unable to load form options");
        } finally {
            setLoadingOptions(false);
        }
    }, []);

    const loadUser = useCallback(async () => {
        const id = normalizeId(userId);
        if (!id) return;
        setLoadingPage(true);
        try {
            const manageUserApiResponse = await getUserById(id);
            const manageUserDetail = manageUserApiResponse?.data;
            if (!manageUserDetail) {
                toast.error(manageUserApiResponse?.message || "User not found");
                return;
            }

            const teamIdsRaw =
                manageUserDetail.teamIds ?? manageUserDetail.teamId ?? [];
            const teamIds = Array.isArray(teamIdsRaw)
                ? teamIdsRaw.map((teamIdValue) =>
                    normalizeId(teamIdValue)
                ).filter(Boolean)
                : [];

            setFormData((previousForm) => ({
                ...previousForm,
                id: normalizeId(manageUserDetail.id) || id,
                userTypeId: normalizeId(manageUserDetail.userTypeId) || "",
                workShiftId: normalizeId(manageUserDetail.workShiftId) || "",
                name: manageUserDetail.name ?? "",
                email: manageUserDetail.email ?? "",
                maskName: manageUserDetail.maskName ?? "",
                phone: toPhoneDigits(manageUserDetail.phone ?? ""),
                phoneExt: manageUserDetail.phoneExt ?? "",
                about: manageUserDetail.about ?? "",
                teamIds,
                status: String(manageUserDetail.status ?? "active").toLowerCase(),
                typeLabel: manageUserDetail.type ?? "",
                blocked: Boolean(manageUserDetail.blocked),
                showClient: Boolean(manageUserDetail.showClient),
                companyIds: Array.isArray(manageUserDetail.companyIds)
                    ? manageUserDetail.companyIds
                    : [],
                clientIds: Array.isArray(manageUserDetail.clientIds)
                    ? manageUserDetail.clientIds
                    : [],
                profileImage:
                    manageUserDetail.profileImage ??
                    manageUserDetail.userImageId ??
                    "",
                profileImageUrl: String(manageUserDetail.userImageUrl ?? "").trim(),
                profileFile: null,
                layout: manageUserDetail.layout ?? "",
                zone: manageUserDetail.zone ?? "",
                password: "",
                repassword: "",
            }));

            if (normalizeId(manageUserDetail.userTypeId)) {
                await applyUserTypeTeamRule(manageUserDetail.userTypeId);
            }
        } catch (loadUserError) {
            toast.error(loadUserError?.message || "Unable to load user");
        } finally {
            setLoadingPage(false);
        }
    }, [userId, applyUserTypeTeamRule]);

    useEffect(() => {
        loadReferenceData();
    }, [loadReferenceData]);

    useEffect(() => {
        if (isEditMode) {
            loadUser();
        } else {
            setLoadingPage(false);
        }
    }, [isEditMode, loadUser]);

    const onUserTypeSelectChange = async (selectedOption) => {
        const value = selectedOption ? String(selectedOption.value) : "";
        setFormData((previousForm) => ({
            ...previousForm,
            userTypeId: value,
        }));
        clearFieldError("userTypeId");
        await applyUserTypeTeamRule(value);
    };

    const updateFormSelectField = (fieldName, selectedOption) => {
        setFormData((previousForm) => ({
            ...previousForm,
            [fieldName]: selectedOption ? String(selectedOption.value) : "",
        }));
        if (manageUserFormSchema.fields[fieldName]) {
            clearFieldError(fieldName);
        }
    };

    const setTeamSelection = (teamId, isChecked) => {
        const normalizedTeamId = normalizeId(teamId);
        setFormData((previousForm) => {
            if (!multipleTeam) {
                return {
                    ...previousForm,
                    teamIds:
                        isChecked && normalizedTeamId ? [normalizedTeamId] : [],
                };
            }
            const nextTeamIdSet = new Set(
                previousForm.teamIds.map(normalizeId)
            );
            if (isChecked && normalizedTeamId)
                nextTeamIdSet.add(normalizedTeamId);
            else nextTeamIdSet.delete(normalizedTeamId);
            return {
                ...previousForm,
                teamIds: Array.from(nextTeamIdSet),
            };
        });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        try {
            await manageUserFormSchema.validate(
                buildManageUserValidationValues(formData),
                { abortEarly: false }
            );
            setErrors({});
        } catch (validationError) {
            const validationErrors = {};
            validationError?.inner?.forEach((errorItem) => {
                if (errorItem?.path && !validationErrors[errorItem.path]) {
                    validationErrors[errorItem.path] = errorItem.message;
                }
            });
            setErrors(validationErrors);
            return;
        }

        const formBody = buildManageUserFormData(formData);
        setSaving(true);
        try {
            const res = isEditMode
                ? await updateUser(normalizeId(userId), formBody)
                : await createUser(formBody);

            if (!isSuccessResponse(res)) {
                toast.error(res?.message || res?.error?.message || "Unable to save user");
                return;
            }

            toast.success(res?.message || (isEditMode ? "User updated" : "User created"));
            navigate(`/${urlEndPoint.manageUser}`);
        } finally {
            setSaving(false);
        }
    };

    const showLoader = loadingOptions || (isEditMode && loadingPage);

    return (
        <div className="page-content manage-user-add-edit">
            <Container fluid>
                <Card className="manage-user-add-edit__card manage-user-add-edit-page border-0 mb-4">
                    <CardBody className="manage-user-add-edit__card-body overflow-visible">
                        <header className="manage-user-add-edit__hero">
                            <div className="manage-user-add-edit__title-block">
                                {/* <p className="manage-user-add-edit__eyebrow mb-0">
                                    {isEditMode ? "Update profile" : "New account"}
                                </p> */}
                                <h1 className="manage-user-add-edit__title">
                                    {isEditMode ? "Edit user" : "Add user"}
                                </h1>
                                {/* <p className="manage-user-add-edit__subtitle">
                                    {isEditMode
                                        ? "Review teams, access, and credentials — then save."
                                        : "Fill in account details, assign teams, and set a password."}
                                </p> */}
                            </div>
                            <Button
                                type="button"
                                color="light"
                                className="manage-user-add-edit__back-btn"
                                onClick={() => navigate(`/${urlEndPoint.manageUser}`)}
                            >
                                <i className="bx bx-arrow-back me-1 align-middle" /> Back to list
                            </Button>
                        </header>

                        {showLoader ? (
                            <Loaders />
                        ) : (
                        <Form onSubmit={handleSubmit}>
                                <Row className="g-4 g-lg-5">
                                    <Col lg={6}>
                                        <div className="manage-user-add-edit__panel">
                                            <p className="manage-user-add-edit__section-title mb-3">
                                                Photo &amp; profile
                                            </p>
                                    <FormGroup className="mb-4 text-center">
                                                <div className="manage-user-add-edit__avatar-wrap">
                                                    <div className="manage-user-add-edit__avatar-ring">
                                                        <div className="manage-user-add-edit__avatar-inner">
                                                            {displayAvatarUrl ? (
                                                                <img
                                                                    src={displayAvatarUrl}
                                                                    alt={formData.name || "User"}
                                                                />
                                                            ) : (
                                                                <i className="fa fa-user" aria-hidden />
                                                )}
                                            </div>
                                                    </div>
                                            <label
                                                htmlFor="fileInput"
                                                        className="manage-user-add-edit__avatar-fab mb-0"
                                                    >
                                                        <i className="fa fa-camera" aria-hidden />
                                            </label>
                                            <input
                                                id="fileInput"
                                                type="file"
                                                accept="image/*"
                                                        className="d-none"
                                                        onChange={(event) => {
                                                            const selectedFile =
                                                                event.target.files?.[0];
                                                            if (selectedFile) {
                                                                setFormData((previousForm) => ({
                                                                    ...previousForm,
                                                                    profileFile: selectedFile,
                                                                    profileImage:
                                                                        selectedFile.name,
                                                                }));
                                                            }
                                                }}
                                            />
                                        </div>
                                                <p className="manage-user-add-edit__hint text-center mb-0">
                                                    JPG or PNG, recommended square image.
                                                </p>
                                    </FormGroup>

                                            <p className="manage-user-add-edit__section-title">
                                                Account details
                                            </p>
                                    <FormGroup className="mb-3">
                                            <Label>
                                                User type <span className="text-danger">*</span>
                                            </Label>
                                            <div className={errors.userTypeId ? "is-invalid" : ""}>
                                                <Select
                                                    options={userTypeSelectOptions}
                                                    className={`select2-selection${errors.userTypeId ? " is-invalid" : ""}`}
                                                    placeholder="Select user type"
                                                    value={
                                                        userTypeSelectOptions.find(
                                                            (opt) =>
                                                                String(opt.value) ===
                                                                String(formData?.userTypeId || "")
                                                        ) || null
                                                    }
                                                    onChange={onUserTypeSelectChange}
                                                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                                    styles={selectZIndexStyles}
                                                    isClearable={false}
                                                />
                                            </div>
                                            {errors.userTypeId ? (
                                                <div className="invalid-feedback d-block">
                                                    {errors.userTypeId}
                                                </div>
                                            ) : null}
                                    </FormGroup>

                                        {formData.typeLabel ? (
                                            <div className="mb-3">
                                                <span className="manage-user-add-edit__role-pill">
                                                    <i className="mdi mdi-shield-account-outline" aria-hidden />
                                                    {formData.typeLabel}
                                                </span>
                                            </div>
                                        ) : null}

                                    <FormGroup className="mb-3">
                                        <Label>
                                            Name <span className="text-danger">*</span>
                                        </Label>
                                            <Input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                invalid={!!errors.name}
                                            />
                                            <FormFeedback>{errors.name}</FormFeedback>
                                    </FormGroup>

                                    <FormGroup className="mb-3">
                                        <Label>
                                            Email <span className="text-danger">*</span>
                                        </Label>
                                            <Input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                invalid={!!errors.email}
                                                readOnly={isEditMode}
                                                style={
                                                    isEditMode
                                                        ? { backgroundColor: "#f1f5f9", cursor: "not-allowed" }
                                                        : undefined
                                                }
                                            />
                                            <FormFeedback>{errors.email}</FormFeedback>
                                            {isEditMode ? (
                                                <small className="manage-user-add-edit__hint">
                                                    Email cannot be changed for this account.
                                                </small>
                                            ) : null}
                                        </FormGroup>

                                        <FormGroup className="mb-3">
                                            <Label>Status</Label>
                                            <Select
                                                options={statusOptions}
                                                className="select2-selection"
                                                placeholder="Select status"
                                                value={
                                                    statusOptions.find(
                                                        (opt) =>
                                                            String(opt.value) ===
                                                            String(formData?.status || "")
                                                    ) || null
                                                }
                                                onChange={(selectedOption) =>
                                                    updateFormSelectField("status", selectedOption)
                                                }
                                                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                                styles={selectZIndexStyles}
                                                isClearable
                                            />
                                    </FormGroup>

                                    <FormGroup className="mb-3">
                                        <Label>About</Label>
                                        <Input
                                            type="textarea"
                                            rows="4"
                                            name="about"
                                            value={formData.about}
                                            onChange={handleChange}
                                        />
                                    </FormGroup>
                                        </div>
                                </Col>

                                    <Col lg={6}>
                                        <div className="manage-user-add-edit__panel">
                                            <p className="manage-user-add-edit__section-title">
                                                Teams &amp; access
                                            </p>
                                        <div className="manage-user-add-edit__teams mb-4">
                                            <h5 className="manage-user-add-edit__teams-title">
                                                <i className="mdi mdi-account-group-outline" aria-hidden />
                                                Assign team
                                            </h5>
                                            {teamOptions.length === 0 ? (
                                                <p className="text-muted small mb-0">No teams loaded.</p>
                                            ) : (
                                        <Row>
                                                    {teamOptions.map((team) => {
                                                        const selected = formData.teamIds
                                                            .map(normalizeId)
                                                            .includes(team.id);
                                                        const teamIdsKey = [...formData.teamIds]
                                                            .map(normalizeId)
                                                            .sort()
                                                            .join("|");
                                                        return (
                                                            <Col md={6} key={team.id} className="manage-user-add-edit__team-item">
                                                    <FormGroup check>
                                                                    {multipleTeam ? (
                                                                        <Input
                                                                            key={`team-cb-${team.id}-${teamIdsKey}`}
                                                            type="checkbox"
                                                                            defaultChecked={selected}
                                                                            onChange={(e) =>
                                                                                setTeamSelection(
                                                                                    team.id,
                                                                                    e.target.checked
                                                                                )
                                                                            }
                                                                        />
                                                                    ) : (
                                                        <Input
                                                                            key={`team-r-${team.id}-${formData.teamIds[0] ?? "x"}`}
                                                                            type="radio"
                                                                            name="singleTeam"
                                                                            value={team.id}
                                                                            defaultChecked={
                                                                                normalizeId(
                                                                                    formData.teamIds[0]
                                                                                ) === team.id
                                                                            }
                                                                            onChange={() =>
                                                                                setFormData((previousForm) => ({
                                                                                    ...previousForm,
                                                                                    teamIds: [team.id],
                                                                                }))
                                                                            }
                                                                        />
                                                                    )}
                                                                    <Label check className="ms-1">
                                                                        {team.name || team.id}
                                                                    </Label>
                                                    </FormGroup>
                                                </Col>
                                                        );
                                                    })}
                                        </Row>
                                            )}
                                    </div>

                                        <p className="manage-user-add-edit__section-title">
                                            Work &amp; contact
                                        </p>
                                    <FormGroup className="mb-3">
                                            <Label>Mask name</Label>
                                        <Input
                                            type="text"
                                            name="maskName"
                                            value={formData.maskName}
                                            onChange={handleChange}
                                        />
                                    </FormGroup>

                                    <FormGroup className="mb-3">
                                            <Label>Work shift</Label>
                                            <Select
                                                options={workShiftSelectOptions}
                                                className="select2-selection"
                                                placeholder="Select shift"
                                                value={
                                                    workShiftSelectOptions.find(
                                                        (opt) =>
                                                            String(opt.value) ===
                                                            String(formData?.workShiftId || "")
                                                    ) || null
                                                }
                                                onChange={(selectedOption) =>
                                                    updateFormSelectField("workShiftId", selectedOption)
                                                }
                                                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                                styles={selectZIndexStyles}
                                                isClearable
                                            />
                                    </FormGroup>

                                    <Row>
                                        <Col md={6}>
                                            <FormGroup className="mb-3">
                                                    <Label>Phone</Label>
                                                    <PatternFormat
                                                        customInput={Input}
                                                        format={MANAGE_USER_PHONE_INPUT_FORMAT}
                                                        allowEmptyFormatting
                                                        name="phone"
                                                        type="text"
                                                        inputMode="numeric"
                                                        value={formData.phone || ""}
                                                        onValueChange={handlePhoneValueChange}
                                                        onPaste={handlePhonePaste}
                                                    />
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup className="mb-3">
                                                    <Label>Phone ext.</Label>
                                                <Input
                                                    type="text"
                                                        name="phoneExt"
                                                        inputMode="numeric"
                                                        pattern="[0-9]*"
                                                        value={formData.phoneExt}
                                                        onChange={handlePhoneExtFieldChange}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                        {/* {isEditMode &&
                                        (formData.companyIds.length > 0 || formData.clientIds.length > 0) ? (
                                            <div className="manage-user-add-edit__meta-chips mb-3">
                                                {formData.companyIds.length > 0 ? (
                                                    <span className="badge bg-light text-dark border">
                                                        Company: {formData.companyIds.join(", ")}
                                                    </span>
                                                ) : null}
                                                {formData.clientIds.length > 0 ? (
                                                    <span className="badge bg-light text-dark border">
                                                        Clients: {formData.clientIds.join(", ")}
                                                    </span>
                                                ) : null}
                                            </div>
                                        ) : null} */}

                                        <p className="manage-user-add-edit__security-label">Security</p>
                                    <Row>
                                        <Col md={6}>
                                            <FormGroup className="mb-3">
                                                    <Label>
                                                        {isEditMode ? (
                                                            "Password (leave blank to keep)"
                                                        ) : (
                                                            <>
                                                                Password{" "}
                                                                <span className="text-danger">*</span>
                                                            </>
                                                        )}
                                                    </Label>
                                                    <InputGroup
                                                        className={
                                                            errors.password ? "has-validation" : ""
                                                        }
                                                    >
                                                <Input
                                                            type={showPassword ? "text" : "password"}
                                                    name="password"
                                                    value={formData.password}
                                                    onChange={handleChange}
                                                            autoComplete="new-password"
                                                            invalid={!!errors.password}
                                                        />
                                                        <Button
                                                            type="button"
                                                            color="light"
                                                            className="border px-2"
                                                            onClick={() => setShowPassword((v) => !v)}
                                                            aria-label={
                                                                showPassword ? "Hide password" : "Show password"
                                                            }
                                                            tabIndex={-1}
                                                        >
                                                            <i
                                                                className={`mdi font-size-18 ${
                                                                    showPassword
                                                                        ? "mdi-eye-off-outline"
                                                                        : "mdi-eye-outline"
                                                                }`}
                                                            />
                                                        </Button>
                                                    </InputGroup>
                                                    <FormFeedback>{errors.password}</FormFeedback>
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup className="mb-3">
                                                    <Label>
                                                        {isEditMode ? (
                                                            "Re-enter password"
                                                        ) : (
                                                            <>
                                                                Re-password{" "}
                                                                <span className="text-danger">*</span>
                                                            </>
                                                        )}
                                                    </Label>
                                                    <InputGroup
                                                        className={
                                                            errors.repassword ? "has-validation" : ""
                                                        }
                                                    >
                                                <Input
                                                            type={showRepassword ? "text" : "password"}
                                                    name="repassword"
                                                    value={formData.repassword}
                                                    onChange={handleChange}
                                                            autoComplete="new-password"
                                                            invalid={!!errors.repassword}
                                                        />
                                                        <Button
                                                            type="button"
                                                            color="light"
                                                            className="border px-2"
                                                            onClick={() => setShowRepassword((v) => !v)}
                                                            aria-label={
                                                                showRepassword
                                                                    ? "Hide re-password"
                                                                    : "Show re-password"
                                                            }
                                                            tabIndex={-1}
                                                        >
                                                            <i
                                                                className={`mdi font-size-18 ${
                                                                    showRepassword
                                                                        ? "mdi-eye-off-outline"
                                                                        : "mdi-eye-outline"
                                                                }`}
                                                            />
                                                        </Button>
                                                    </InputGroup>
                                                    <FormFeedback>{errors.repassword}</FormFeedback>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                        </div>
                                </Col>
                            </Row>

                                <div className="manage-user-add-edit__footer">
                                    <Button
                                        type="button"
                                        color="light"
                                        className="manage-user-add-edit__back-btn"
                                        onClick={() => navigate(`/${urlEndPoint.manageUser}`)}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        color="primary"
                                        type="submit"
                                        className="manage-user-add-edit__submit"
                                        disabled={saving}
                                    >
                                        {saving ? "Saving…" : isEditMode ? "Save changes" : "Create user"}
                                </Button>
                            </div>
                        </Form>
                        )}
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default ManageUserAddEdit;
